import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { missingCatalogResult, type FavoriteProductResponse, type ParsedPriceTag, type PriceCompareResponse, type PriceTagResolveResponse, type PriceTagProductCandidate } from "./types";
import {
  getWebApp,
  getInitData,
  getStartParam,
  getTelegramLanguageCode,
  initTelegramApp,
  triggerHapticSuccess,
  triggerHapticError,
  sendTelegramData,
} from "./telegram";
import { ScannerGatewayClient, getFriendlyRpcErrorMessage, type GatewayConnectionState, type RpcClientError } from "./ws/client";
import { PriceCache } from "./scanner/priceCache";
import { useBarcodeScanner } from "./scanner/useBarcodeScanner";
import { getSoundEnabled, setSoundEnabled, playScanSuccessBeep } from "./scanner/sound";
import { validateBarcode } from "./scanner/eanValidator";
import { scannerReducer, initialScannerModel, canScan, canPreviewCamera } from "./state/scannerMachine";
import {
  getSavedBatchItems,
  saveBatchItems,
  clearSavedBatchItems,
  getSavedStoreId,
  saveStoreId,
  getSavedStartToken,
  saveStartToken,
  clearSavedStartToken,
  getSavedLanguage,
  saveLanguage,
  getSavedScanHistory,
  upsertScanHistoryItem,
  migrateOfflineQueueToHistory,
  type LocalScanHistoryItem,
} from "./state/storage";
import { Overlay } from "./overlay/Overlay";
import { Viewfinder } from "./overlay/Viewfinder";
import { ConnectionBanner } from "./overlay/ConnectionBanner";
import { ScanHistoryPanel } from "./overlay/ScanHistoryPanel";
import type { ScanMode } from "./overlay/ModeSwitch";
import { RecognitionModeSwitch, type RecognitionMode } from "./overlay/RecognitionModeSwitch";
import { BatchPanel, type BatchItem } from "./overlay/BatchPanel";
import { LowLightPrompt } from "./overlay/LowLightPrompt";
import { TorchToggle } from "./overlay/TorchToggle";
import { ManualBarcodeInput } from "./overlay/ManualBarcodeInput";
import { SoundToggle } from "./overlay/SoundToggle";
import { ZoomControls } from "./overlay/ZoomControls";
import { LanguageSwitch } from "./overlay/LanguageSwitch";
import { StoreSelect } from "./overlay/StoreSelect";
import type { Language } from "./i18n";
import { resolveLanguage, t } from "./i18n";
import { recognizePriceTagFromVideo, PriceTagRecognizeError } from "./scanner/priceTag/recognize";
import { toPriceTagResolvePayload } from "./scanner/priceTag/types";
import { usePriceTagDebug } from "./scanner/priceTag/usePriceTagDebug";
import { debugError, type DebugUpload } from "./scanner/priceTag/debug";
import { PriceTagDebugPanel } from "./overlay/PriceTagDebugPanel";

const GATEWAY_URL =
  (import.meta.env.VITE_SCANNER_GATEWAY_WS_URL as string | undefined) ||
  (import.meta.env.VITE_SCANNER_GATEWAY_URL as string | undefined) ||
  "wss://gw.sod.ee";
const ERROR_DISPLAY_MS = 7_000;
const PRICE_TAG_ENABLED = import.meta.env.VITE_SCANNER_PRICE_TAG_ENABLED !== "false";

export function App() {
  const [language, setLanguage] = useState<Language>(() => {
    const saved = getSavedLanguage();
    if (saved) return saved;
    const webApp = getWebApp();
    const tgLang = getTelegramLanguageCode(webApp);
    return resolveLanguage({
      saved,
      telegram: tgLang,
      browser: typeof navigator !== "undefined" ? navigator.language : undefined,
    });
  });
  const [model, dispatch] = useReducer(scannerReducer, initialScannerModel);
  const [currentStoreId, setCurrentStoreId] = useState<string | undefined>(getSavedStoreId);
  const [isSoundEnabled, setIsSoundEnabled] = useState<boolean>(getSoundEnabled);
  const [batchItems, setBatchItems] = useState<BatchItem[]>(getSavedBatchItems);
  const [scanMode] = useState<ScanMode>("single");
  const [recognitionMode, setRecognitionMode] = useState<RecognitionMode>("barcode");
  const [isPriceTagBusy, setIsPriceTagBusy] = useState(false);
  const lastPriceTagDebugIdRef = useRef<string | undefined>(undefined);
  const [isBatchFlash, setIsBatchFlash] = useState<boolean>(false);
  const [isManualInputOpen, setIsManualInputOpen] = useState<boolean>(false);
  const [errorFeedback, setErrorFeedback] = useState<{ isError: boolean; message: string | null }>({
    isError: false,
    message: null,
  });

  const [isWsConnected, setIsWsConnected] = useState<boolean>(false);
  const [isNavigatorOnline, setIsNavigatorOnline] = useState<boolean>(() =>
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );

  const [scanHistory, setScanHistory] = useState<LocalScanHistoryItem[]>(() => migrateOfflineQueueToHistory());
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [returnToHistory, setReturnToHistory] = useState(false);
  const [favoritedIds, setFavoritedIds] = useState<Record<string, boolean>>({});
  const [isFavoriteBusy, setIsFavoriteBusy] = useState(false);

  const scannerContainerRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const clientRef = useRef<ScannerGatewayClient | null>(null);
  const uploadPriceTagDebug = useCallback<DebugUpload>(async (entry) => {
    if (!clientRef.current) throw new Error("Gateway unavailable");
    return clientRef.current.request("scan.recordDebug", { entry });
  }, []);
  const priceTagDebug = usePriceTagDebug(uploadPriceTagDebug, isWsConnected);
  const { begin: beginPriceTagDebug, record: recordPriceTagDebug } = priceTagDebug;
  const cacheRef = useRef(new PriceCache());
  const modelStateRef = useRef(model.state);
  const currentStoreIdRef = useRef(currentStoreId);
  const scanModeRef = useRef(scanMode);
  const languageRef = useRef(language);
  const recognitionModeRef = useRef(recognitionMode);
  const lastPriceTagRef = useRef<ParsedPriceTag | null>(null);
  const expiryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const batchFlashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const errorFlashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resultOriginRef = useRef<"scan" | "history">("scan");

  modelStateRef.current = model.state;
  currentStoreIdRef.current = currentStoreId;
  scanModeRef.current = scanMode;
  languageRef.current = language;
  recognitionModeRef.current = recognitionMode;

  const isOffline = !isNavigatorOnline || !isWsConnected || model.state === "OFFLINE";

  const handleLanguageChange = useCallback((newLang: Language) => {
    setLanguage(newLang);
    saveLanguage(newLang);
  }, []);

  // Listen to browser online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsNavigatorOnline(true);
    };
    const handleOffline = () => {
      setIsNavigatorOnline(false);
      dispatch({ type: "GATEWAY_OFFLINE" });
    };
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Persist batchItems to localStorage whenever changed
  useEffect(() => {
    saveBatchItems(batchItems);
  }, [batchItems]);

  // Persist currentStoreId to localStorage whenever changed
  useEffect(() => {
    saveStoreId(currentStoreId);
  }, [currentStoreId]);

  // Direct WebSocket connection to gateway with startToken & initData
  useEffect(() => {
    const webApp = getWebApp();
    if (!webApp) {
      dispatch({ type: "AUTH_FAILED", message: t(languageRef.current, "auth_telegram_only") });
      return;
    }
    initTelegramApp(webApp);

    const startToken = getStartParam(webApp) || getSavedStartToken();
    const initData = getInitData(webApp);
    if (!startToken || !initData) {
      dispatch({ type: "AUTH_FAILED", message: t(languageRef.current, "auth_missing_session") });
      return;
    }
    saveStartToken(startToken);

    let authenticated = false;
    let cancelled = false;

    const client = new ScannerGatewayClient(
      GATEWAY_URL,
      { startToken, initData },
      (state: GatewayConnectionState) => {
        if (cancelled) return;
        if (state === "open") {
          setIsWsConnected(true);
          if (!authenticated) {
            authenticated = true;
            dispatch({ type: "AUTH_SUCCEEDED" });
            dispatch({ type: "CAMERA_READY" });
          } else if (modelStateRef.current === "OFFLINE") {
            dispatch({ type: "GATEWAY_RECONNECTED" });
          }
        } else if (state === "reconnecting" || state === "closed") {
          setIsWsConnected(false);
          if (authenticated) {
            dispatch({ type: "GATEWAY_OFFLINE" });
          }
        }
      },
      (error) => {
        if (cancelled) return;
        client.stop();
        clearSavedStartToken();
        const msg =
          error.code === "SESSION_EXPIRED"
            ? t(languageRef.current, "auth_session_expired")
            : error.message || t(languageRef.current, "auth_failed");
        dispatch({ type: "AUTH_FAILED", message: msg });
      },
      (sessionData) => {
        if (cancelled) return;
        if (!authenticated) {
          authenticated = true;
          dispatch({ type: "AUTH_SUCCEEDED" });
          dispatch({ type: "CAMERA_READY" });
        }
        const exp = (sessionData as { exp?: number; expiresAt?: string | number } | undefined)?.exp;
        if (typeof exp === "number") {
          const msUntilExpiry = exp * 1000 - Date.now();
          if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
          expiryTimerRef.current = setTimeout(() => {
            client.stop();
            clearSavedStartToken();
            dispatch({ type: "LOOKUP_FAILED", code: "SESSION_EXPIRED" });
          }, Math.max(0, msUntilExpiry));
        }
      },
    );

    clientRef.current = client;
    client.connect();

    const resume = () => {
      if (document.visibilityState && document.visibilityState !== "visible") return;
      client.resume();
    };
    document.addEventListener("visibilitychange", resume);
    window.addEventListener("pageshow", resume);
    window.addEventListener("focus", resume);
    webApp.onEvent?.("activated", resume);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", resume);
      window.removeEventListener("pageshow", resume);
      window.removeEventListener("focus", resume);
      if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
      if (batchFlashTimerRef.current) clearTimeout(batchFlashTimerRef.current);
      if (errorFlashTimerRef.current) clearTimeout(errorFlashTimerRef.current);
      clientRef.current?.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-dismiss errors only. A found/opened product stays until the user closes it.
  useEffect(() => {
    if (model.state !== "NOT_FOUND" && model.state !== "ERROR") return;
    const timer = setTimeout(() => dispatch({ type: "RESUME_SCANNING" }), ERROR_DISPLAY_MS);
    return () => clearTimeout(timer);
  }, [model.state]);

  const handleManualEntry = useCallback(() => {
    setIsManualInputOpen(true);
  }, []);

  const toggleSound = useCallback(() => {
    setIsSoundEnabled((prev) => {
      const next = !prev;
      setSoundEnabled(next);
      return next;
    });
  }, []);

  const handleBatchRemove = useCallback((id: string) => {
    setBatchItems((prev) => {
      const updated = prev.filter((item) => item.id !== id);
      saveBatchItems(updated);
      return updated;
    });
  }, []);

  const handleIncrementQuantity = useCallback((id: string) => {
    setBatchItems((prev) => {
      const updated = prev.map((item) => {
        if (item.id !== id) return item;
        return { ...item, quantity: (item.quantity ?? 1) + 1 };
      });
      saveBatchItems(updated);
      return updated;
    });
  }, []);

  const handleDecrementQuantity = useCallback((id: string) => {
    setBatchItems((prev) => {
      const updated = prev.map((item) => {
        if (item.id !== id) return item;
        const cur = item.quantity ?? 1;
        return { ...item, quantity: Math.max(1, cur - 1) };
      });
      saveBatchItems(updated);
      return updated;
    });
  }, []);

  const handleBatchClear = useCallback(() => {
    setBatchItems([]);
    clearSavedBatchItems();
  }, []);

  const handleBatchSubmit = useCallback(() => {
    if (batchItems.length === 0) return;
    const barcodes: string[] = [];
    for (const item of batchItems) {
      const qty = item.quantity ?? 1;
      for (let i = 0; i < qty; i++) {
        barcodes.push(item.barcode);
      }
    }
    triggerHapticSuccess();
    sendTelegramData(barcodes);
  }, [batchItems]);

  const triggerErrorFeedback = useCallback((message: string) => {
    triggerHapticError();
    setErrorFeedback({ isError: true, message });
    if (errorFlashTimerRef.current) clearTimeout(errorFlashTimerRef.current);
    errorFlashTimerRef.current = setTimeout(() => {
      setErrorFeedback({ isError: false, message: null });
    }, 1500);
  }, []);

  const rememberMissingCatalog = useCallback((barcode: string, name?: string) => {
    const stub = missingCatalogResult(barcode, name);
    cacheRef.current.set(barcode, stub, Date.now());
    setScanHistory(upsertScanHistoryItem(barcode, { name: stub.product.name, lookedUp: true, lastResult: stub }));
    dispatch({ type: "LOOKUP_SUCCESS", result: stub });
  }, []);

  const processBarcode = useCallback((rawBarcode: string, options?: { quiet?: boolean }) => {
    const curLang = languageRef.current;
    const validation = validateBarcode(rawBarcode, curLang);
    if (!validation.isValid) {
      triggerErrorFeedback(validation.error || t(curLang, "val_checksum_invalid"));
      return;
    }
    const barcode = validation.normalized;
    const currentlyOffline = !navigator.onLine || !isWsConnected || modelStateRef.current === "OFFLINE";

    if (scanModeRef.current === "batch") {
      // In batch mode: add barcode or increment quantity in cart with sound/vibe feedback
      triggerHapticSuccess();
      if (isSoundEnabled) {
        playScanSuccessBeep();
      }
      setIsBatchFlash(true);
      if (batchFlashTimerRef.current) clearTimeout(batchFlashTimerRef.current);
      batchFlashTimerRef.current = setTimeout(() => setIsBatchFlash(false), 400);

      const cached = cacheRef.current.get(barcode, Date.now());

      setBatchItems((prev) => {
        const existingIdx = prev.findIndex((item) => item.barcode === barcode);
        let updated: BatchItem[];
        if (existingIdx >= 0) {
          updated = prev.map((item, idx) =>
            idx === existingIdx
              ? {
                  ...item,
                  quantity: (item.quantity ?? 1) + 1,
                  name: item.name || cached?.product?.name,
                  price: item.price !== undefined ? item.price : cached?.best?.price,
                  currency: item.currency || "€",
                }
              : item,
          );
        } else {
          const newItem: BatchItem = {
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            barcode,
            quantity: 1,
            name: cached?.product?.name,
            price: cached?.best?.price,
            currency: "€",
          };
          updated = [...prev, newItem];
        }
        saveBatchItems(updated);
        return updated;
      });

      // If online and not yet in cache, asynchronously resolve product name & best price in background
      if (!cached && clientRef.current && !currentlyOffline) {
        clientRef.current
          .request<PriceCompareResponse>("price.compare", {
            barcode,
            currentStoreId: currentStoreIdRef.current,
          })
          .then((result) => {
            cacheRef.current.set(barcode, result, Date.now());
            setBatchItems((prev) => {
              const updated = prev.map((item) =>
                item.barcode === barcode
                  ? {
                      ...item,
                      name: item.name || result.product?.name,
                      price: item.price !== undefined ? item.price : result.best?.price,
                      currency: item.currency || "€",
                    }
                  : item,
              );
              saveBatchItems(updated);
              return updated;
            });
          })
          .catch(() => {
            // Non-critical background lookup failure
          });
      }

      if (currentlyOffline) {
        setScanHistory(upsertScanHistoryItem(barcode, { lookedUp: false }));
      }
      return;
    }

    // Offline single scan: keep the code in history with a Search button. Do not auto-sync later.
    if (currentlyOffline) {
      triggerHapticSuccess();
      if (isSoundEnabled) {
        playScanSuccessBeep();
      }
      setIsBatchFlash(true);
      if (batchFlashTimerRef.current) clearTimeout(batchFlashTimerRef.current);
      batchFlashTimerRef.current = setTimeout(() => setIsBatchFlash(false), 400);

      setScanHistory(upsertScanHistoryItem(barcode, { lookedUp: false }));
      return;
    }

    // In single mode when online: perform online price comparison lookup
    resultOriginRef.current = options?.quiet ? "history" : "scan";
    if (!options?.quiet) {
      setReturnToHistory(false);
      setHistoryExpanded(false);
      dispatch({ type: "BARCODE_DETECTED" });
      triggerHapticSuccess();
      if (isSoundEnabled) {
        playScanSuccessBeep();
      }
    }

    const cached = cacheRef.current.get(barcode, Date.now());
    if (cached) {
      if (cached.favorited && cached.product?.id) {
        setFavoritedIds((prev) => ({ ...prev, [cached.product.id]: true }));
      }
      setScanHistory(upsertScanHistoryItem(barcode, { name: cached.product?.name, lookedUp: true, lastResult: cached }));
      if (!options?.quiet) {
        dispatch({ type: "LOOKUP_START" });
      }
      dispatch({ type: "LOOKUP_SUCCESS", result: cached });
      return;
    }

    const client = clientRef.current;
    if (!client) {
      setScanHistory(upsertScanHistoryItem(barcode, { lookedUp: false }));
      return;
    }

    dispatch({ type: "LOOKUP_START" });
    client
      .request<PriceCompareResponse>("price.compare", { barcode, currentStoreId: currentStoreIdRef.current })
      .then((result) => {
        cacheRef.current.set(barcode, result, Date.now());
        if (result.favorited && result.product?.id) {
          setFavoritedIds((prev) => ({ ...prev, [result.product.id]: true }));
        }
        setScanHistory(upsertScanHistoryItem(barcode, { name: result.product?.name, lookedUp: true, lastResult: result }));
        dispatch({ type: "LOOKUP_SUCCESS", result });
      })
      .catch((error: RpcClientError) => {
        if (error.code === "TRANSPORT_CLOSED") {
          setScanHistory(upsertScanHistoryItem(barcode, { lookedUp: false }));
          dispatch({ type: "GATEWAY_OFFLINE" });
          return;
        }
        if (error.code === "PRODUCT_NOT_FOUND" || error.code === "NO_PRICE_DATA") {
          rememberMissingCatalog(barcode);
          return;
        }
        const friendlyMsg = getFriendlyRpcErrorMessage(error.code, languageRef.current, error.message);
        triggerErrorFeedback(friendlyMsg);
        dispatch({ type: "LOOKUP_FAILED", code: error.code });
      });
  }, [isSoundEnabled, isWsConnected, triggerErrorFeedback, rememberMissingCatalog]);

  const handleDetected = useCallback((barcode: string) => {
    if (recognitionModeRef.current !== "barcode") return;
    if (modelStateRef.current !== "SCANNING" && modelStateRef.current !== "OFFLINE") return;
    processBarcode(barcode);
  }, [processBarcode]);

  const applyPriceTagLookup = useCallback((response: PriceTagResolveResponse) => {
    if (response.comparison) {
      const barcode = response.product?.ean || response.barcode || response.comparison.product.barcode;
      if (response.comparison.favorited && response.comparison.product?.id) {
        setFavoritedIds((prev) => ({ ...prev, [response.comparison!.product.id]: true }));
      }
      if (barcode) {
        cacheRef.current.set(barcode, response.comparison, Date.now());
        setScanHistory(upsertScanHistoryItem(barcode, { name: response.comparison.product.name, lookedUp: true, lastResult: response.comparison }));
      }
      dispatch({ type: "LOOKUP_SUCCESS", result: response.comparison });
      return;
    }
    if (response.alternatives && response.alternatives.length > 0) {
      dispatch({ type: "LOOKUP_CANDIDATES", candidates: response.alternatives });
      return;
    }
    dispatch({ type: "LOOKUP_FAILED", code: "PRODUCT_NOT_MATCHED" });
  }, []);

  const lookupParsedPriceTag = useCallback(async (tag: ParsedPriceTag, productId?: string, debugId?: string) => {
    const started = performance.now();
    recordPriceTagDebug(debugId, { stage: "lookup", status: "started", elapsedMs: 0, data: { storeId: currentStoreIdRef.current, productId } });
    const client = clientRef.current;
    if (!client) {
      recordPriceTagDebug(debugId, { stage: "lookup", status: "error", elapsedMs: performance.now() - started, error: { name: "AGENT_UNAVAILABLE", message: "Gateway client unavailable" } });
      dispatch({ type: "LOOKUP_FAILED", code: "AGENT_UNAVAILABLE" });
      return;
    }
    dispatch({ type: "LOOKUP_START" });
    try {
      const response = await client.request<PriceTagResolveResponse>(
        "priceTag.resolve",
        toPriceTagResolvePayload(tag, { storeId: currentStoreIdRef.current, productId }),
      );
      recordPriceTagDebug(debugId, {
        stage: "lookup", status: "success", elapsedMs: performance.now() - started,
        data: { recognition: response.recognition, product: response.product, alternatives: response.alternatives, parsed: response.parsed },
      });
      applyPriceTagLookup(response);
    } catch (error) {
      recordPriceTagDebug(debugId, { stage: "lookup", status: "error", elapsedMs: performance.now() - started, error: debugError(error) });
      const rpcError = error as RpcClientError;
      if (rpcError.code === "TRANSPORT_CLOSED") {
        dispatch({ type: "GATEWAY_OFFLINE" });
        return;
      }
      const barcode = tag.barcode?.trim();
      if (barcode && (rpcError.code === "PRODUCT_NOT_FOUND" || rpcError.code === "NO_PRICE_DATA")) {
        rememberMissingCatalog(barcode, tag.productName);
        return;
      }
      const friendlyMsg = getFriendlyRpcErrorMessage(rpcError.code, languageRef.current, rpcError.message);
      triggerErrorFeedback(friendlyMsg);
      dispatch({ type: "LOOKUP_FAILED", code: rpcError.code });
    }
  }, [applyPriceTagLookup, triggerErrorFeedback, rememberMissingCatalog, recordPriceTagDebug]);

  const handlePriceTagCapture = useCallback(async () => {
    if (recognitionModeRef.current !== "priceTag" || isPriceTagBusy) return;
    if (modelStateRef.current !== "SCANNING") return;
    const video = videoRef.current;
    if (!video) return;
    setIsPriceTagBusy(true);
    const debugAttempt = beginPriceTagDebug(currentStoreIdRef.current);
    lastPriceTagDebugIdRef.current = debugAttempt?.id;
    dispatch({ type: "LOOKUP_START" });
    try {
      const tag = await recognizePriceTagFromVideo(video, currentStoreIdRef.current, debugAttempt?.observer);
      lastPriceTagRef.current = tag;
      triggerHapticSuccess();
      if (isSoundEnabled) playScanSuccessBeep();
      await lookupParsedPriceTag(tag, undefined, debugAttempt?.id);
    } catch (error) {
      const code =
        error instanceof PriceTagRecognizeError
          ? error.code === "NO_FRAME"
            ? "NO_TEXT_DETECTED"
            : error.code
          : "NO_TEXT_DETECTED";
      const friendlyMsg = getFriendlyRpcErrorMessage(code, languageRef.current);
      triggerErrorFeedback(friendlyMsg);
      dispatch({ type: "LOOKUP_FAILED", code });
    } finally {
      setIsPriceTagBusy(false);
    }
  }, [isPriceTagBusy, isSoundEnabled, lookupParsedPriceTag, triggerErrorFeedback, beginPriceTagDebug]);

  const handleSelectCandidate = useCallback((candidate: PriceTagProductCandidate) => {
    const tag = lastPriceTagRef.current ?? { rawText: candidate.name, productName: candidate.name, barcode: candidate.ean };
    void lookupParsedPriceTag(tag, candidate.productId, lastPriceTagDebugIdRef.current);
  }, [lookupParsedPriceTag]);

  const handleRecognitionModeChange = useCallback((mode: RecognitionMode) => {
    setRecognitionMode(mode);
    dispatch({ type: "RESUME_SCANNING" });
  }, []);

  const handleManualSubmit = useCallback((barcode: string) => {
    setIsManualInputOpen(false);
    processBarcode(barcode);
  }, [processBarcode]);

  const handleHistorySearch = useCallback((barcode: string) => {
    resultOriginRef.current = "history";
    setReturnToHistory(true);
    const historyItem = scanHistory.find((item) => item.barcode === barcode);
    const cached = cacheRef.current.get(barcode, Date.now()) ?? historyItem?.lastResult ?? null;
    const hadRealResult = Boolean(cached && !cached.missing && cached.product?.id);
    const initial = cached ?? missingCatalogResult(barcode, historyItem?.name);

    if (initial.favorited && initial.product?.id) {
      setFavoritedIds((prev) => ({ ...prev, [initial.product.id]: true }));
    }
    setScanHistory(
      upsertScanHistoryItem(barcode, {
        name: initial.missing ? historyItem?.name ?? initial.product.name : initial.product.name,
        lookedUp: true,
        lastResult: initial,
      }),
    );
    dispatch({ type: "LOOKUP_SUCCESS", result: initial });

    if (!clientRef.current || !navigator.onLine || !isWsConnected) return;
    clientRef.current
      .request<PriceCompareResponse>("price.compare", {
        barcode,
        currentStoreId: currentStoreIdRef.current,
      })
      .then((result) => {
        cacheRef.current.set(barcode, result, Date.now());
        setScanHistory(upsertScanHistoryItem(barcode, { name: result.product?.name, lookedUp: true, lastResult: result }));
        if (resultOriginRef.current === "history") {
          dispatch({ type: "LOOKUP_SUCCESS", result });
        }
      })
      .catch((error: RpcClientError) => {
        if (hadRealResult) return;
        if (error.code !== "PRODUCT_NOT_FOUND" && error.code !== "NO_PRICE_DATA") return;
        const stub = missingCatalogResult(barcode, historyItem?.name);
        cacheRef.current.set(barcode, stub, Date.now());
        setScanHistory(upsertScanHistoryItem(barcode, { name: stub.product.name, lookedUp: true, lastResult: stub }));
        if (resultOriginRef.current === "history") {
          dispatch({ type: "LOOKUP_SUCCESS", result: stub });
        }
      });
  }, [scanHistory, isWsConnected]);

  const handleFavorite = useCallback(async () => {
    const result = model.result;
    const client = clientRef.current;
    if (!result?.product?.id || !client || isFavoriteBusy) return;

    setIsFavoriteBusy(true);
    try {
      const response = await client.request<FavoriteProductResponse>("product.favorite", {
        productId: result.product.id,
        productName: result.product.name,
        shopSlug: result.best.storeId,
        barcode: result.product.barcode,
      });
      setFavoritedIds((prev) => ({ ...prev, [result.product.id]: response.favorited }));
      triggerHapticSuccess();
    } catch {
      triggerErrorFeedback(t(languageRef.current, "rpc_FALLBACK"));
    } finally {
      setIsFavoriteBusy(false);
    }
  }, [model.result, isFavoriteBusy, triggerErrorFeedback]);

  const {
    error: cameraError,
    hasTorch,
    isTorchOn,
    isLowLight,
    toggleTorch,
    zoom,
    minZoom,
    maxZoom,
    stepZoom,
    setZoom,
    resetZoom,
    restartCamera,
  } = useBarcodeScanner(videoRef, {
    enabled: canPreviewCamera(model.state),
    onDetected: handleDetected,
  });

  const [hasVideo, setHasVideo] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const markPlaying = () => setHasVideo(true);
    const markStopped = () => setHasVideo(false);
    video.addEventListener("playing", markPlaying);
    video.addEventListener("pause", markStopped);
    video.addEventListener("emptied", markStopped);
    return () => {
      video.removeEventListener("playing", markPlaying);
      video.removeEventListener("pause", markStopped);
      video.removeEventListener("emptied", markStopped);
    };
  }, []);

  const handleStartCamera = useCallback(() => {
    dispatch({ type: "RETRY_CAMERA" });
    void videoRef.current?.play?.().catch(() => undefined);
    restartCamera();
  }, [restartCamera]);

  useEffect(() => {
    if (cameraError) {
      if (cameraError.type === "permission_denied") {
        dispatch({ type: "CAMERA_PERMISSION_DENIED" });
      } else if (cameraError.type === "not_found") {
        dispatch({ type: "CAMERA_NOT_FOUND" });
      } else {
        dispatch({ type: "CAMERA_ERROR", message: cameraError.message });
      }
    }
  }, [cameraError]);

  const isScanningActive = canScan(model.state);
  const isLiveScanFeedback =
    resultOriginRef.current === "scan" && (model.state === "DETECTED" || model.state === "LOOKUP");
  const isSuccessState = scanMode === "batch" ? isBatchFlash : (isBatchFlash || isLiveScanFeedback);

  const isFatalOverlayState =
    model.state === "AUTHENTICATING" ||
    model.state === "AUTH_ERROR" ||
    model.state === "CAMERA_READY" ||
    model.state === "CAMERA_PERMISSION_DENIED" ||
    model.state === "CAMERA_NOT_FOUND" ||
    model.state === "SESSION_EXPIRED";

  const showConnectionBanner =
    isOffline &&
    model.state !== "AUTHENTICATING" &&
    model.state !== "AUTH_ERROR";

  const showScanHistory =
    scanHistory.length > 0 &&
    scanMode === "single" &&
    recognitionMode === "barcode" &&
    model.state !== "RESULT" &&
    model.state !== "LOOKUP" &&
    model.state !== "DETECTED" &&
    model.state !== "CONFIRM" &&
    model.state !== "AUTHENTICATING" &&
    model.state !== "AUTH_ERROR";
  const currentProductId = model.result?.product?.id;
  const isCurrentFavorited = Boolean(currentProductId && favoritedIds[currentProductId]);

  useEffect(() => {
    if (!isWsConnected || !isNavigatorOnline) return;
    const client = clientRef.current;
    if (!client) return;
    const leftovers = getSavedScanHistory().filter((item) => !item.lookedUp);
    for (const item of leftovers) {
      client
        .request("scan.recordHistory", {
          barcode: item.barcode,
          productName: item.name || item.barcode,
        })
        .catch(() => undefined);
    }
  }, [isWsConnected, isNavigatorOnline]);

  // Touch pinch-to-zoom gesture support
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;

  useEffect(() => {
    const el = scannerContainerRef.current;
    if (!el || !isScanningActive) return;

    let initialDist = 0;
    let startZoom = 1;

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        const p1 = e.touches[0];
        const p2 = e.touches[1];
        initialDist = Math.hypot(p1.clientX - p2.clientX, p1.clientY - p2.clientY);
        startZoom = zoomRef.current;
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && initialDist > 0) {
        if (e.cancelable) {
          e.preventDefault();
        }
        const p1 = e.touches[0];
        const p2 = e.touches[1];
        const currentDist = Math.hypot(p1.clientX - p2.clientX, p1.clientY - p2.clientY);
        const factor = currentDist / initialDist;
        const targetZoom = Math.min(Math.max(startZoom * factor, minZoom), maxZoom);
        setZoom(Number(targetZoom.toFixed(2)));
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) {
        initialDist = 0;
      }
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [isScanningActive, minZoom, maxZoom, setZoom]);

  return (
    <div ref={scannerContainerRef} className="scanner">
      <video ref={videoRef} className="scanner__video" playsInline muted autoPlay />
      <Viewfinder
        active={isScanningActive}
        isSuccess={isSuccessState}
        isError={errorFeedback.isError}
        errorMessage={errorFeedback.message}
        isOffline={isOffline && model.state !== "AUTHENTICATING" && model.state !== "AUTH_ERROR"}
        language={language}
        variant={recognitionMode === "priceTag" ? "priceTag" : "barcode"}
      />

      {showConnectionBanner && <ConnectionBanner isOffline={isOffline} language={language} />}

      {scanMode === "single" ? (
        <Overlay
          model={model}
          onRetryCamera={handleStartCamera}
          onManualEntry={handleManualEntry}
          onFavorite={model.state === "RESULT" && !model.result?.missing ? handleFavorite : undefined}
          onBack={
            model.state === "RESULT" && returnToHistory
              ? () => {
                  dispatch({ type: "RESUME_SCANNING" });
                  setReturnToHistory(false);
                  setHistoryExpanded(true);
                }
              : undefined
          }
          onClose={
            model.state === "RESULT" || model.state === "CONFIRM"
              ? () => {
                  dispatch({ type: "RESUME_SCANNING" });
                  setReturnToHistory(false);
                  setHistoryExpanded(false);
                }
              : undefined
          }
          onSelectCandidate={handleSelectCandidate}
          isFavorited={isCurrentFavorited}
          isFavoriteBusy={isFavoriteBusy}
          language={language}
          recognitionMode={recognitionMode}
        />
      ) : (
        isFatalOverlayState && (
          <Overlay model={model} onRetryCamera={handleStartCamera} onManualEntry={handleManualEntry} language={language} />
        )
      )}

      {showScanHistory && (
        <ScanHistoryPanel
          items={scanHistory}
          expanded={historyExpanded}
          onExpandedChange={setHistoryExpanded}
          onSearch={handleHistorySearch}
          language={language}
        />
      )}

      {!hasVideo && canPreviewCamera(model.state) && (
        <button
          type="button"
          className="scanner__start-camera"
          onClick={handleStartCamera}
          data-testid="start-camera-button"
        >
          <span className="scanner__start-camera-icon" aria-hidden="true">📷</span>
          <span>{t(language, "cam_start_hint")}</span>
        </button>
      )}

      {PRICE_TAG_ENABLED && recognitionMode === "priceTag" && hasVideo && model.state === "SCANNING" && (
        <button
          type="button"
          className="price-tag-capture"
          onClick={() => void handlePriceTagCapture()}
          disabled={isPriceTagBusy || isOffline}
          data-testid="price-tag-capture"
          aria-label={t(language, "price_tag_capture_aria")}
        >
          {t(language, "price_tag_capture")}
        </button>
      )}

      <div className="scanner__toolbar">
        <div className="scanner__toolbar-cluster">
          {canPreviewCamera(model.state) && (
            <>
              <TorchToggle
                hasTorch={hasTorch}
                isTorchOn={isTorchOn}
                onToggle={toggleTorch}
                language={language}
              />
              <SoundToggle enabled={isSoundEnabled} onToggle={toggleSound} language={language} />
              {recognitionMode === "barcode" && (
                <button
                  type="button"
                  className="manual-button"
                  onClick={() => setIsManualInputOpen(true)}
                  aria-label={t(language, "manual_entry_aria")}
                  data-testid="manual-entry-button"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="16" rx="2" />
                    <line x1="7" y1="8" x2="7" y2="16" />
                    <line x1="10" y1="8" x2="10" y2="16" />
                    <line x1="13" y1="8" x2="13" y2="16" />
                    <line x1="17" y1="8" x2="17" y2="16" />
                  </svg>
                  <span>{t(language, "manual_entry_btn")}</span>
                </button>
              )}
              {recognitionMode === "priceTag" && (
                <StoreSelect
                  value={currentStoreId}
                  onChange={(storeId) => {
                    setCurrentStoreId(storeId);
                    saveStoreId(storeId);
                  }}
                  language={language}
                />
              )}
            </>
          )}
        </div>
        <div className="scanner__toolbar-cluster">
          {PRICE_TAG_ENABLED && recognitionMode === "priceTag" && (
            <PriceTagDebugPanel
              language={language} enabled={priceTagDebug.enabled} onToggle={priceTagDebug.toggle}
              entries={priceTagDebug.entries} frame={priceTagDebug.frame} onClear={priceTagDebug.clear}
              busy={isPriceTagBusy} storageFailed={priceTagDebug.storageFailed}
            />
          )}
          <LanguageSwitch language={language} onChange={handleLanguageChange} />
        </div>
        {PRICE_TAG_ENABLED && canPreviewCamera(model.state) && (
          <div className="scanner__toolbar-mode">
            <RecognitionModeSwitch mode={recognitionMode} onChange={handleRecognitionModeChange} language={language} />
          </div>
        )}
      </div>

      {isScanningActive && (
        <>

          <LowLightPrompt
            visible={isScanningActive && hasTorch && !isTorchOn && isLowLight}
            onEnableTorch={toggleTorch}
            language={language}
          />

          {model.state !== "RESULT" && (
            <ZoomControls
              zoom={zoom}
              minZoom={minZoom}
              maxZoom={maxZoom}
              stepZoom={stepZoom}
              isBatchMode={false}
              onZoomChange={setZoom}
              onResetZoom={resetZoom}
              language={language}
            />
          )}

          {scanMode === "batch" && (
            <BatchPanel
              items={batchItems}
              onRemove={handleBatchRemove}
              onIncrementQuantity={handleIncrementQuantity}
              onDecrementQuantity={handleDecrementQuantity}
              onClear={handleBatchClear}
              onSubmit={handleBatchSubmit}
              language={language}
            />
          )}
        </>
      )}

      <ManualBarcodeInput
        isOpen={isManualInputOpen}
        onClose={() => setIsManualInputOpen(false)}
        onSubmit={handleManualSubmit}
        language={language}
      />
    </div>
  );
}
